"use client";

import { MessageCircle, Share2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/hooks/use-analytics";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

type SocialShareProps = {
  productUrl: string;
  productTitle: string;
  className?: string;
  showHeading?: boolean;
  compact?: boolean;
};

// Helper functions for platform-specific share URLs
const getFacebookShareUrl = (url: string): string => {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
};

const getTwitterShareUrl = (url: string, text: string): string => {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
};

const getWhatsAppShareUrl = (url: string, text: string): string => {
  return `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
};

export function SocialShare({
  productUrl,
  productTitle,
  className,
  showHeading = true,
  compact = false,
}: SocialShareProps) {
  const [isSharing, setIsSharing] = useState(false);
  const { trackSocialShare } = useAnalytics();

  // Check if Web Share API is supported
  const isWebShareSupported =
    typeof navigator !== "undefined" && "share" in navigator;

  // Handle native share
  const handleNativeShare = useCallback(async () => {
    if (!isWebShareSupported) return;

    setIsSharing(true);
    try {
      await navigator.share({
        title: productTitle,
        text: "Check out this supplement from WillPower Store",
        url: productUrl,
      });

      // Track successful share
      trackSocialShare("native", productUrl, productTitle);
      logger.info("Native share succeeded", { productUrl, productTitle });
    } catch (error) {
      // Ignore AbortError (user cancelled)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      // Log other errors silently
      logger.error(
        "Share failed (native)",
        error instanceof Error ? error : new Error(String(error)),
        { productUrl, productTitle }
      );
    } finally {
      setIsSharing(false);
    }
  }, [productUrl, productTitle, trackSocialShare, isWebShareSupported]);

  // Handle platform-specific shares
  const handlePlatformShare = useCallback(
    (platform: "facebook" | "twitter" | "whatsapp") => {
      const shareText = "Check out this supplement from WillPower Store";
      let shareUrl: string;

      if (platform === "facebook") {
        shareUrl = getFacebookShareUrl(productUrl);
      } else if (platform === "twitter") {
        shareUrl = getTwitterShareUrl(productUrl, shareText);
      } else if (platform === "whatsapp") {
        shareUrl = getWhatsAppShareUrl(productUrl, shareText);
      } else {
        // Defensive fallback for unexpected values — log and use the direct URL
        logger.warn("Unexpected share platform, falling back to product URL", {
          platform,
          productUrl,
        });
        shareUrl = productUrl;
      }

      // Open share URL in new window
      try {
        window.open(shareUrl, "_blank", "width=600,height=400");
        // Track platform share
        trackSocialShare(platform, productUrl, productTitle);
        logger.info("Platform share opened", {
          platform,
          productUrl,
          productTitle,
        });
      } catch (error) {
        logger.error(
          "Share failed (platform)",
          error instanceof Error ? error : new Error(String(error)),
          { platform, productUrl, productTitle }
        );
      }
    },
    [productUrl, productTitle, trackSocialShare]
  );

  return (
    <div className={cn("space-y-3", className)}>
      {showHeading && (
        <h3 className="text-muted-foreground text-sm">Share this product</h3>
      )}

      {isWebShareSupported ? (
        // Native share button (compact supports icon-only)
        <div>
          {compact ? (
            <Button
              aria-label={`Share ${productTitle}`}
              className="p-2"
              disabled={isSharing}
              onClick={handleNativeShare}
              size="icon"
              type="button"
              variant="ghost"
            >
              <Share2 className="h-4 w-4" />
              <span className="sr-only">Share</span>
            </Button>
          ) : (
            <Button
              aria-label="Share this product"
              className="flex items-center gap-2"
              disabled={isSharing}
              onClick={handleNativeShare}
              size="sm"
              type="button"
              variant="ghost"
            >
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          )}
        </div>
      ) : (
        // Fallback platform buttons
        <div className="flex items-center gap-2">
          <Button
            aria-label="Share on Facebook"
            className={compact ? "p-2" : "flex items-center gap-2"}
            onClick={() => handlePlatformShare("facebook")}
            size={compact ? "icon" : "sm"}
            type="button"
            variant="ghost"
          >
            <Share2 className="h-4 w-4" />
            {compact ? <span className="sr-only">Facebook</span> : "Facebook"}
          </Button>
          <Button
            aria-label="Share on Twitter"
            className={compact ? "p-2" : "flex items-center gap-2"}
            onClick={() => handlePlatformShare("twitter")}
            size={compact ? "icon" : "sm"}
            type="button"
            variant="ghost"
          >
            <Share2 className="h-4 w-4" />
            {compact ? <span className="sr-only">Twitter</span> : "Twitter"}
          </Button>
          <Button
            aria-label="Share on WhatsApp"
            className={compact ? "p-2" : "flex items-center gap-2"}
            onClick={() => handlePlatformShare("whatsapp")}
            size={compact ? "icon" : "sm"}
            type="button"
            variant="ghost"
          >
            <MessageCircle className="h-4 w-4" />
            {compact ? <span className="sr-only">WhatsApp</span> : "WhatsApp"}
          </Button>
        </div>
      )}
    </div>
  );
}
